import React, { useEffect } from "react"
import types from "prop-types"
import Rails from "@rails/ujs"
import { CONVERSATIONS } from "utils/featuresHelpers"
import { useSelector } from "react-redux"
import { isFeatureEnabled } from "reduxSlices/featuresSlice"
import { tracked } from "reduxSlices/formSubmissionSlice"
import Modal, { ModalHeader } from "shared/Modal"
import { PrimaryButton, TertiaryButton } from "shared/buttons"
import { HiddenInput } from "shared/inputs"
import { Checkbox } from "shared/checkboxes"
import { useFormSubmissionExportContext } from "contexts/FormSubmissionExportContext"

const DataExportModal = ({
  closeModal,
  exportFormSubmissionData,
  modalIsOpen,
}) => {
  const {
    canSubmitExport,
    deselectConversations,
    deselectResolution,
    deselectTask,
    deselectFormSubmission,
    formSubmissionHasTasks,
    includeFormSubmission,
    includeConversations,
    includeResolution,
    includedTaskIds,
    selectConversations,
    selectResolution,
    selectTask,
    selectAllTasks,
    selectFormSubmission,
    taskSelected,
    tasks,
  } = useFormSubmissionExportContext()

  const conversationsFeatureEnabled = useSelector(isFeatureEnabled(CONVERSATIONS))
  const formSubmissionIsTracked = useSelector(tracked)
  const showConversationsOptions = conversationsFeatureEnabled && formSubmissionIsTracked

  useEffect(() => {
    selectAllTasks()
  }, [selectAllTasks, tasks])

  const close = () => {
    selectFormSubmission()
    selectConversations()
    selectResolution()
    selectAllTasks()
    closeModal()
  }

  const submit = (event) => {
    event.preventDefault()
    exportFormSubmissionData(includeFormSubmission, includeConversations, includeResolution, includedTaskIds)()
    selectFormSubmission()
    selectConversations()
    selectResolution()
    selectAllTasks()
    closeModal()
  }

  return (
    <Modal isOpen={modalIsOpen}>
      <ModalHeader
        closeModal={close}
        heading="Export"
      />
      <form onSubmit={submit}>
        <HiddenInput name="authenticity_token" value={Rails.csrfToken()} />

        <p className="font-bold mb-2">Form Submission Options</p>
        <div className="mb-4">
          <label htmlFor="form-submission-export-checkbox">
            <Checkbox
              className="mr-2 cursor-pointer"
              uuid="form-submission-export-checkbox"
              value={includeFormSubmission}
              onChange={includeFormSubmission ? deselectFormSubmission : selectFormSubmission}
            />
            <span>Include form submission data</span>
          </label>
        </div>

        <p className="font-bold mb-2">Activities</p>
        <div className="flex flex-col mb-4">
          {formSubmissionHasTasks ? (
            tasks.map(({ id, title }) => (
              <label key={id} htmlFor="all-tasks-export-checkbox">
                <Checkbox
                  className="mr-2 cursor-pointer"
                  uuid={id.toString()}
                  value={taskSelected(id)}
                  onChange={taskSelected(id) ? () => deselectTask(id) : () => selectTask(id)}
                />
                <span>{title}</span>
              </label>
            ))
          ) : (
            <p>No exportable activities for submission</p>
          )}
        </div>

        {
          showConversationsOptions
          && (
            <>
              <p className="font-bold mb-2">Conversations</p>
              <div className="flex flex-col mb-4">
                <label htmlFor="conversations-export-checkbox">
                  <Checkbox
                    className="mr-2 cursor-pointer"
                    uuid="conversations-export-checkbox"
                    value={includeConversations}
                    onChange={includeConversations ? deselectConversations : selectConversations}
                  />
                  <span>Include all conversations</span>
                </label>

                <label htmlFor="resolution-export-checkbox">
                  <Checkbox
                    className="mr-2 cursor-pointer"
                    uuid="resolution-export-checkbox"
                    value={includeResolution}
                    onChange={includeResolution ? deselectResolution : selectResolution}
                  />
                  <span>Include resolution</span>
                </label>
              </div>
            </>
          )
        }

        <div className="flex justify-center mt-8 mb-10">
          <TertiaryButton
            text="Cancel"
            onClick={close}
            className="mr-2"
          />
          <PrimaryButton
            text="Export"
            type="submit"
            disabled={!canSubmitExport}
          />
        </div>
      </form>
    </Modal>
  )
}

DataExportModal.propTypes = {
  closeModal: types.func.isRequired,
  exportFormSubmissionData: types.func.isRequired,
  modalIsOpen: types.bool.isRequired,
}

export default DataExportModal
